import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import NeuralField from "./NeuralField";
import { Container, Eyebrow, Display, Lead, Arrow, useReveal, useInViewPlayback } from "./ui";
import { colors, fonts, media } from "../../styles/theme";
import { explore } from "../../pages/landingContent";
import { verticals } from "../../pages/verticalContent";
import { posterFor } from "../../vid/posters";

/* ---------- Who it's for ---------- */

const FocusWrap = styled.section`
  padding: 120px 0 140px;
  background: linear-gradient(180deg, ${colors.limestone} 0%, ${colors.sand} 100%);
`;

const FocusHead = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 56px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const PrimaryGrid = styled.div`
  display: grid;
  gap: 20px;

  @media ${media.medium} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

// Readability on photos: a deep green shade over the lower part of every card keeps text at
// accessible contrast while the top of the image stays clear. Bright footage is dimmed a touch.
const cardMedia = css`
  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${(p) => p.$focus || "center"};
    filter: saturate(0.9) sepia(0.05) brightness(${(p) => (p.$bright ? 0.8 : 1)});
    transition: transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  &:hover video {
    transform: scale(1.06);
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(30, 42, 34, 0) 25%,
      rgba(30, 42, 34, 0.55) 52%,
      rgba(30, 42, 34, 0.88) 78%,
      rgba(30, 42, 34, 0.94) 100%
    );
  }
`;

// Brighter copper for highlighted words on photos, with a soft shadow to lift it off any image.
const onPhotoText = css`
  text-shadow: 0 1px 14px rgba(20, 28, 22, 0.45);

  em {
    color: #f0c394;
  }
`;

// Wave backgrounds fade out behind the text so they sit above it, not through it.
const FieldMedia = styled.div`
  position: absolute;
  inset: 0;
  -webkit-mask-image: linear-gradient(180deg, #000 0%, #000 30%, transparent 62%);
  mask-image: linear-gradient(180deg, #000 0%, #000 30%, transparent 62%);
`;

const BigCard = styled(Link)`
  position: relative;
  min-height: 520px;
  border-radius: 28px;
  overflow: hidden;
  background: ${colors.forest};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 32px;
  transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  ${cardMedia}

  &:hover {
    transform: translateY(-6px);
  }

  @media ${media.large} {
    min-height: 620px;
    padding: 44px;
  }
`;

const CardBody = styled.div`
  position: relative;
  z-index: 2;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 7px 14px;
  border-radius: 999px;
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.linen};
  background: rgba(20, 28, 22, 0.55);
  border: 1px solid rgba(250, 247, 241, 0.22);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

const CardTitle = styled.h3`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(36px, 3.8vw, 56px);
  line-height: 1;
  letter-spacing: -0.035em;
  color: ${colors.linen};
  margin: 18px 0 14px;
  ${onPhotoText}

  em {
    display: block;
    font-style: normal;
  }
`;

const CardText = styled.p`
  font-family: ${fonts.body};
  font-size: 18px;
  line-height: 1.55;
  color: rgba(250, 247, 241, 0.9);
  max-width: 460px;
  text-shadow: 0 1px 10px rgba(20, 28, 22, 0.4);
`;

const Explore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.linen};

  svg {
    transition: transform 0.3s ease;
  }
  a:hover & svg {
    transform: translateX(5px);
  }
`;

// Four smaller areas under the two big cards: two by two on tablets, one row on desktop.
const SmallGrid = styled.div`
  display: grid;
  gap: 16px;
  margin-top: 20px;

  @media ${media.small} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media ${media.large} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const SmallCard = styled(Link)`
  position: relative;
  min-height: 340px;
  border-radius: 24px;
  overflow: hidden;
  background: ${colors.forestSoft};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 26px;
  transition: transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
  ${cardMedia}

  &:hover {
    transform: translateY(-6px);
  }
`;

const SmallTitle = styled.h4`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: 30px;
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: ${colors.linen};
  margin-top: 14px;
  ${onPhotoText}

  em {
    font-style: normal;
  }
`;

const CardVideo = ({ src, position }) => {
  const ref = useRef(null);
  useInViewPlayback(ref);
  return <video ref={ref} src={src} poster={posterFor(src)} muted loop playsInline preload="metadata" aria-hidden="true" style={position ? { objectPosition: position } : undefined} />;
};

const CardMedia = ({ v }) =>
  v.video ? (
    <CardVideo src={v.video} position={v.videoPosition} />
  ) : (
    <FieldMedia aria-hidden="true">
      <NeuralField variant="dark" calm={0.3} lines={8} />
    </FieldMedia>
  );

// Footage bright enough to need dimming behind text.
const BRIGHT = ["education", "workplace-safety", "military"];

const firstSentence = (text) => {
  const i = text.indexOf(". ");
  return i === -1 ? text : text.slice(0, i + 1);
};

const PRIMARY = ["neurodivergent", "mental-health"];

export const FocusAreas = () => {
  const scope = useRef(null);
  useReveal(scope);
  const primary = verticals.filter((v) => PRIMARY.includes(v.slug));
  const more = verticals.filter((v) => !PRIMARY.includes(v.slug));
  return (
    <FocusWrap id="focus" ref={scope} aria-labelledby="focus-title">
      <Container>
        <FocusHead>
          <div data-reveal>
            <Eyebrow $color={colors.terracotta}>{explore.eyebrow}</Eyebrow>
            <Display id="focus-title" style={{ marginTop: 20 }}>
              {explore.titleLead} <em>{explore.titleEmphasis}</em>
            </Display>
          </div>
          <Lead data-reveal="0.1">{explore.body}</Lead>
        </FocusHead>

        <PrimaryGrid>
          {primary.map((v, i) => (
            <BigCard key={v.slug} to={`/${v.slug}`} data-reveal={i * 0.1}>
              <CardMedia v={v} />
              <CardBody>
                <Tag>{v.nav}</Tag>
                <CardTitle>
                  {v.titleLead} <em>{v.titleEmphasis}</em>
                </CardTitle>
                <CardText>{firstSentence(v.body)}</CardText>
                <Explore>
                  Explore {v.nav.toLowerCase()} <Arrow />
                </Explore>
              </CardBody>
            </BigCard>
          ))}
        </PrimaryGrid>

        <SmallGrid>
          {more.map((v, i) => (
            <SmallCard key={v.slug} to={`/${v.slug}`} data-reveal={i * 0.08} $bright={BRIGHT.includes(v.slug)}>
              <CardMedia v={v} />
              <CardBody>
                <Tag>{v.nav}</Tag>
                <SmallTitle>
                  {v.titleLead} <em>{v.titleEmphasis}</em>
                </SmallTitle>
                <Explore>
                  Explore <Arrow />
                </Explore>
              </CardBody>
            </SmallCard>
          ))}
        </SmallGrid>
      </Container>
    </FocusWrap>
  );
};
